/**
 * ControllingKpiPage — Zentrale Kennzahlen-Übersicht für Geschäftsführung + Controlling.
 *
 * 4 KPI-Bereiche × 4 Cards = 16 KPIs mit Sparkline-Trends und Erklärungs-Panels.
 *
 * @page /controlling/kpi
 * @roles administrator, ops_manager
 * @since v2.191.0
 */
import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Info, X, ExternalLink, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const api = (path) => fetch(path, { credentials: 'include' }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const fmt = (n) => n != null ? Number(n).toLocaleString('de-AT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—';
const fmt1 = (n) => n != null ? Number(n).toLocaleString('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—';
const fmt2 = (n) => n != null ? Number(n).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const MONAT_NAMEN = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];


// ─── Sparkline SVG ───
function Sparkline({ values, width = 60, height = 20, color = '#338edc' }) {
  if (!values || values.length < 2) return null;
  const filtered = values.filter(v => v != null);
  if (filtered.length < 2) return null;
  const min = Math.min(...filtered);
  const max = Math.max(...filtered);
  const range = max - min || 1;
  const step = width / (filtered.length - 1);
  const points = filtered.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}


// ─── KPI Card with Info Panel ───
function KpiCard({ label, value, sub, trend, trendValues, info, link, linkLabel, t }) {
  const [showInfo, setShowInfo] = useState(false);
  const navigate = useNavigate();

  const trendColor = trend > 0 ? '#22C55E' : trend < 0 ? '#EF4444' : 'var(--text-tertiary,#6B7280)';
  const trendPrefix = trend > 0 ? '+' : '';

  return (
    <div style={{ position: 'relative', padding: '16px 20px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, background: 'var(--bg-card,#fff)', minWidth: 0 }}>
      {/* Info icon */}
      {info && (
        <button onClick={() => setShowInfo(!showInfo)}
          style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted,#9CA3AF)', opacity: 0.6 }}
          title={t('ckpi.formel')}>
          <Info size={14} />
        </button>
      )}

      <div style={{ fontSize: 12, color: 'var(--text-tertiary,#6B7280)', marginBottom: 4, paddingRight: 20 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary,#1A1A2E)', lineHeight: 1.2 }}>{value}</div>

      {/* Sparkline + Trend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, minHeight: 20 }}>
        {trendValues && <Sparkline values={trendValues} />}
        {trend != null && <span style={{ fontSize: 12, fontWeight: 500, color: trendColor }}>{trendPrefix}{fmt1(trend)}%</span>}
      </div>

      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted,#9CA3AF)', marginTop: 2 }}>{sub}</div>}

      {/* Info Panel */}
      {showInfo && info && (
        <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 8, border: '1px solid #DBEAFE', background: '#EFF6FF', fontSize: 12, color: '#1E40AF', position: 'relative' }}>
          <button onClick={() => setShowInfo(false)} style={{ position: 'absolute', top: 6, right: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#1E40AF' }}>
            <X size={12} />
          </button>
          {info.formel && (
            <div style={{ marginBottom: 8 }}>
              <strong>{t('ckpi.formel')}:</strong><br />
              <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{info.formel}</span>
            </div>
          )}
          {info.quellen && (
            <div style={{ marginBottom: 8 }}>
              <strong>{t('ckpi.datenquellen')}:</strong>
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {info.quellen.map((q, i) => <li key={i} style={{ marginBottom: 2 }}>{q}</li>)}
              </ul>
            </div>
          )}
          {info.interpretation && (
            <div style={{ marginBottom: link ? 8 : 0 }}>
              <strong>{t('ckpi.interpretation')}:</strong><br />
              {info.interpretation}
            </div>
          )}
          {link && (
            <button onClick={() => navigate(link)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 12, fontWeight: 500, padding: 0 }}>
              <ExternalLink size={12} /> {linkLabel || t('ckpi.detailsIn', { modul: '' })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Section Header ───
function SectionHeader({ title }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted,#9CA3AF)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 12, marginTop: 8 }}>
      {title}
    </div>
  );
}


// ═══════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════
export default function ControllingKpiPage() {
  const { t } = useLanguage();
  const now = new Date();
  const [jahr, setJahr] = useState(now.getFullYear());
  const [monat, setMonat] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api(`/api/controlling/kpi-dashboard?jahr=${jahr}&monat=${monat}`),
      api(`/api/controlling/kpi-trend?monate=4`),
    ]).then(([d, tr]) => {
      setData(d);
      setTrend(tr.trend || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [jahr, monat]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => { if (monat === 1) { setJahr(j => j - 1); setMonat(12); } else setMonat(m => m - 1); };
  const nextMonth = () => { if (monat === 12) { setJahr(j => j + 1); setMonat(1); } else setMonat(m => m + 1); };

  // Extract trend arrays for sparklines
  const trendUmsatz = trend.map(t => t.umsatz);
  const trendDb = trend.map(t => t.db);
  const trendMarge = trend.map(t => t.marge_pct);
  const trendPakete = trend.map(t => t.pakete);
  const trendCpp = trend.map(t => t.kosten_pro_paket);

  const u = data?.umsatz || {};
  const db = data?.deckungsbeitrag || {};
  const pk = data?.personalkosten || {};
  const ff = data?.fulfillment || {};
  const ef = data?.effizienz || {};
  const jv = data?.jv || {};
  const pa = data?.partner || {};

  // Marge change vs prev month
  const margeChange = db.gesamt?.marge_pct != null && db.gesamt?.marge_prev_pct != null
    ? Math.round((db.gesamt.marge_pct - db.gesamt.marge_prev_pct) * 10) / 10
    : null;

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary,#1A1A2E)', margin: 0, marginBottom: 4 }}>{t('ckpi.title')}</h1>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary,#6B7280)' }}>{t('ckpi.subtitle')}</div>
        </div>

        {/* Month Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={prevMonth} style={{ padding: '8px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <ChevronLeft size={16} />
          </button>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', minWidth: 100, textAlign: 'center' }}>
            {MONAT_NAMEN[monat]} {jahr}
          </div>
          <button onClick={nextMonth} style={{ padding: '8px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <ChevronRight size={16} />
          </button>
          {loading && <RefreshCw size={16} style={{ color: 'var(--text-muted,#9CA3AF)', animation: 'spin 1s linear infinite' }} />}
        </div>
      </div>

      {/* ═══ BEREICH 1: UMSATZ & ERTRAG ═══ */}
      <SectionHeader title={t('ckpi.umsatzErtrag')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
        <KpiCard
          label={t('ckpi.umsatzGesamt')}
          value={`€${fmt(u.gesamt)}`}
          sub={`EB €${fmt(u.eigenbetrieb)} / JV €${fmt(u.jv)}`}
          trend={u.veraenderung_pct}
          trendValues={trendUmsatz}
          info={{
            formel: `Σ deckungsbeitrag_cache.umsatz_gesamt (${MONAT_NAMEN[monat]} ${jahr})`,
            quellen: ['deckungsbeitrag_cache (billing_date)', 'standorte.betriebsmodell (EB/JV Split)'],
            interpretation: u.veraenderung_pct > 0 ? `${t('ckpi.vsVormonat')}: +${fmt1(u.veraenderung_pct)}%` : u.veraenderung_pct != null ? `${t('ckpi.vsVormonat')}: ${fmt1(u.veraenderung_pct)}%` : null
          }}
          link="/deckungsbeitrag" linkLabel={t('ckpi.detailsIn', { modul: 'Deckungsbeitrag' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.dbGesamt')}
          value={`€${fmt(db.gesamt?.db)}`}
          sub={`EB €${fmt(db.eigenbetrieb?.db)} / JV €${fmt(db.jv?.db)}`}
          trendValues={trendDb}
          info={{
            formel: 'Umsatz − Kosten (Personal + Verpackung + Carrier)',
            quellen: ['deckungsbeitrag_cache.db_gesamt', 'EB: betriebsmodell=eigen, JV: betriebsmodell=joint_venture'],
            interpretation: `Eigenbetrieb-DB: €${fmt(db.eigenbetrieb?.db)}, JV-DB: €${fmt(db.jv?.db)}`
          }}
          link="/deckungsbeitrag" linkLabel={t('ckpi.detailsIn', { modul: 'Deckungsbeitrag' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.dbMarge')}
          value={db.gesamt?.marge_pct != null ? `${fmt1(db.gesamt.marge_pct)}%` : '—'}
          sub={`EB ${fmt1(db.eigenbetrieb?.marge_pct)}% / JV ${fmt1(db.jv?.marge_pct)}%`}
          trend={margeChange}
          trendValues={trendMarge}
          info={{
            formel: `DB Gesamt ÷ Umsatz Gesamt = €${fmt(db.gesamt?.db)} ÷ €${fmt(u.gesamt)}`,
            quellen: ['deckungsbeitrag_cache (aggregiert)'],
            interpretation: db.gesamt?.marge_pct >= 30 ? 'Gute Marge (≥30%)' : db.gesamt?.marge_pct >= 20 ? 'Akzeptable Marge (20–30%)' : 'Marge unter Ziel (<20%)'
          }}
          link="/deckungsbeitrag" linkLabel={t('ckpi.detailsIn', { modul: 'Deckungsbeitrag' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.veraenderung')}
          value={u.veraenderung_pct != null ? `${u.veraenderung_pct > 0 ? '+' : ''}${fmt1(u.veraenderung_pct)}%` : '—'}
          sub={t('ckpi.vsVormonat')}
          trend={u.veraenderung_pct}
          info={{
            formel: '(Umsatz aktuell − Umsatz Vormonat) ÷ Umsatz Vormonat × 100',
            quellen: ['deckungsbeitrag_cache (aktueller + vorheriger Monat)'],
            interpretation: u.veraenderung_pct > 5 ? 'Starkes Wachstum' : u.veraenderung_pct > 0 ? 'Leichtes Wachstum' : u.veraenderung_pct != null && u.veraenderung_pct < 0 ? 'Rückgang' : null
          }}
          t={t}
        />
      </div>

      {/* ═══ BEREICH 2: PERSONALKOSTEN ═══ */}
      <SectionHeader title={t('ckpi.personalkosten')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
        <KpiCard
          label={t('ckpi.planSatz')}
          value={pk.stundensatz_plan != null ? `€${fmt2(pk.stundensatz_plan)}/h` : '—'}
          sub={`PLAN: €${fmt(pk.plan_gesamt)} / ${fmt1(pk.soll_stunden)}h`}
          info={{
            formel: `PLAN-Kosten ÷ SOLL-Stunden = €${fmt(pk.plan_gesamt)} ÷ ${fmt1(pk.soll_stunden)}h`,
            quellen: ['personalkosten_jahresplanung (Monats-Brutto + AG-NK)', 'mitarbeiter.wochenstunden_soll × 4,33 (aus mitarbeiter_arbeitszeiten aggregiert)'],
            interpretation: 'Kalkulierter Stundensatz auf Basis der Jahresplanung'
          }}
          link="/personalkosten" linkLabel={t('ckpi.detailsIn', { modul: 'Personalkosten' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.istSatz')}
          value={pk.stundensatz_ist != null ? `€${fmt2(pk.stundensatz_ist)}/h` : '—'}
          sub={pk.abweichung_pct != null ? `${pk.abweichung_pct > 0 ? '+' : ''}${fmt1(pk.abweichung_pct)}% vs PLAN` : null}
          trend={pk.abweichung_pct != null ? -pk.abweichung_pct : null}
          info={{
            formel: `PLAN-Kosten ÷ Produktive Stunden = €${fmt(pk.plan_gesamt)} ÷ ${fmt1(pk.productive_stunden)}h`,
            quellen: ['personalkosten_* (Kostenbasis)', 'pack_stats + waves (produktive Stunden)', 'gap-classifier.js (Idle-Bereinigung, 30s Schwelle)'],
            interpretation: pk.stundensatz_ist > pk.stundensatz_plan ? `Liegt über PLAN (€${fmt2(pk.stundensatz_plan)}/h). Hauptursache: Auslastung unter Plan.` : 'Im Plan oder darunter — gute Kosteneffizienz.'
          }}
          link="/personalkosten" linkLabel={t('ckpi.detailsIn', { modul: 'Personalkosten-Monitor' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.auslastung')}
          value={`${fmt1(pk.auslastungsgrad)}%`}
          sub={`${fmt1(pk.productive_stunden)}h / ${fmt1(pk.soll_stunden)}h`}
          trend={pk.auslastungsgrad >= 60 ? Math.abs(pk.auslastungsgrad - 70) : -(70 - pk.auslastungsgrad)}
          info={{
            formel: `Produktive Stunden ÷ SOLL-Stunden = ${fmt1(pk.productive_stunden)}h ÷ ${fmt1(pk.soll_stunden)}h`,
            quellen: ['pack_stats + waves (produktive Arbeit)', 'gap-classifier.js (Transition < 30s = produktiv)', 'mitarbeiter.wochenstunden_soll × 4,33 (SOLL, aus mitarbeiter_arbeitszeiten)'],
            interpretation: pk.auslastungsgrad >= 70 ? 'Guter Bereich (≥70%)' : pk.auslastungsgrad >= 50 ? 'Ausbaufähig (50–70%)' : 'Deutlich unter Ziel — hohe Leerkosten'
          }}
          link="/personalkosten" linkLabel={t('ckpi.detailsIn', { modul: 'Personalkosten-Monitor' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.leerkosten')}
          value={`€${fmt(pk.leerkosten)}`}
          sub={pk.plan_gesamt > 0 ? `${fmt1(pk.leerkosten / pk.plan_gesamt * 100)}% der PK` : null}
          info={{
            formel: `PK-Gesamt × (Idle-Stunden ÷ Anwesenheits-Stunden) = €${fmt(pk.plan_gesamt)} × ${fmt1(pk.idle_stunden)}h / ${fmt1(pk.productive_stunden + pk.idle_stunden)}h`,
            quellen: ['gap-classifier.js (Idle = Lücken 30s–120min)', 'personalkosten_jahresplanung (Kostenbasis)'],
            interpretation: pk.leerkosten > 10000 ? 'Signifikante Leerkosten — Auslastungsoptimierung empfohlen' : 'Leerkosten im akzeptablen Rahmen'
          }}
          link="/personalkosten" linkLabel={t('ckpi.detailsIn', { modul: 'Personalkosten-Monitor' })}
          t={t}
        />
      </div>

      {/* ═══ BEREICH 3: FULFILLMENT & EFFIZIENZ ═══ */}
      <SectionHeader title={t('ckpi.fulfillmentEffizienz')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
        <KpiCard
          label={t('ckpi.pakete')}
          value={fmt(ff.pakete_gesamt)}
          sub={`EB ${fmt(ff.pakete_eigen)} / JV ${fmt(ff.pakete_jv)}`}
          trendValues={trendPakete}
          info={{
            formel: `Σ pack_stats.package_count (${MONAT_NAMEN[monat]} ${jahr})`,
            quellen: ['pack_stats (start_time)', 'kunden → standorte.betriebsmodell (EB/JV Split)'],
            interpretation: `${fmt(ff.pakete_gesamt)} Pakete verarbeitet. EB: ${fmt(ff.pakete_eigen)}, JV: ${fmt(ff.pakete_jv)}`
          }}
          link="/performance" linkLabel={t('ckpi.detailsIn', { modul: 'Performance' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.produktivitaet')}
          value={`${fmt1(ef.produktivitaet_pct)}%`}
          sub={`Idle: ${fmt1(ef.idle_pct)}%`}
          info={{
            formel: `Produktive Stunden ÷ SOLL-Stunden × 100`,
            quellen: ['pack_stats + waves (Arbeitszeit)', 'gap-classifier.js (produktive Transition)'],
            interpretation: ef.produktivitaet_pct >= 70 ? 'Gute Produktivität (≥70%)' : 'Unter Zielwert — Optimierungspotenzial bei Idle-Zeit'
          }}
          link="/idle-time" linkLabel={t('ckpi.detailsIn', { modul: 'Idle-Time' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.kostenProPaket')}
          value={pk.kosten_pro_paket != null ? `€${fmt2(pk.kosten_pro_paket)}` : '—'}
          sub={`${fmt(pk.pakete)} ${t('ckpi.pakete')}`}
          trendValues={trendCpp}
          info={{
            formel: `PK-Gesamt ÷ Pakete = €${fmt(pk.plan_gesamt)} ÷ ${fmt(pk.pakete)}`,
            quellen: ['personalkosten_jahresplanung (Kostenbasis)', 'pack_stats.package_count (Pakete)'],
            interpretation: pk.kosten_pro_paket != null ? `€${fmt2(pk.kosten_pro_paket)} Personalkosten pro Paket` : 'Keine Daten'
          }}
          link="/personalkosten" linkLabel={t('ckpi.detailsIn', { modul: 'Personalkosten-Monitor' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.forecastAcc')}
          value={ff.forecast_accuracy != null ? `${fmt1(ff.forecast_accuracy)}%` : '—'}
          sub={ff.forecast_quality_score != null ? `Quality Score: ${fmt1(ff.forecast_quality_score)}` : null}
          info={{
            formel: 'Ø Accuracy = Ø(1 − |IST − SOLL| / SOLL) × 100 pro Kunde/Tag',
            quellen: ['forecast_quality (tägliche Scores)', 'forecast_snapshots (IST-Daten)'],
            interpretation: ff.forecast_accuracy >= 85 ? 'Gute Prognosegenauigkeit (≥85%)' : ff.forecast_accuracy != null ? 'Verbesserungspotenzial bei der Prognose' : 'Noch keine Forecast-Quality-Daten'
          }}
          link="/forecast/quality" linkLabel={t('ckpi.detailsIn', { modul: 'Forecast Quality' })}
          t={t}
        />
      </div>

      {/* ═══ BEREICH 4: JV-STANDORT ═══ */}
      <SectionHeader title={t('ckpi.jvStandort')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 28 }}>
        <KpiCard
          label={t('ckpi.jvUmsatz')}
          value={`€${fmt(jv.umsatz)}`}
          sub={u.gesamt > 0 ? `${fmt1(jv.umsatz / u.gesamt * 100)}% vom Gesamt` : null}
          info={{
            formel: `Σ deckungsbeitrag_cache WHERE standorte.betriebsmodell = 'joint_venture'`,
            quellen: ['deckungsbeitrag_cache + standorte'],
            interpretation: `JV-Umsatz macht ${u.gesamt > 0 ? fmt1(jv.umsatz / u.gesamt * 100) : '?'}% des Gesamtumsatzes aus`
          }}
          link="/jv-deckungsbeitrag" linkLabel={t('ckpi.detailsIn', { modul: 'JV-Deckungsbeitrag' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.revshare')}
          value={`€${fmt(jv.revshare)}`}
          sub={jv.hdl_umsatz > 0 ? `${fmt1(jv.revshare / jv.hdl_umsatz * 100)}% vom HDL-Umsatz` : null}
          info={{
            formel: 'Personalkosten-Anteil der JV-Kunden (= FC-RevShare)',
            quellen: ['deckungsbeitrag_cache.kosten_personal (JV-gefiltert)', 'finecom_revshare (Sätze)'],
            interpretation: 'RevShare = Finecom-Anteil an den Handling-Kosten'
          }}
          link="/jv-deckungsbeitrag" linkLabel={t('ckpi.detailsIn', { modul: 'JV-Deckungsbeitrag' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.weshipDb')}
          value={`€${fmt(jv.weship_db)}`}
          sub={jv.weship_marge_pct != null ? `${fmt1(jv.weship_marge_pct)}% Marge` : null}
          info={{
            formel: `JV-Umsatz − JV-Kosten = €${fmt(jv.umsatz)} − Kosten`,
            quellen: ['deckungsbeitrag_cache.db_gesamt (JV-gefiltert)'],
            interpretation: `WeShip-Anteil am JV-Ertrag: €${fmt(jv.weship_db)} (${fmt1(jv.weship_marge_pct)}% Marge)`
          }}
          link="/jv-deckungsbeitrag" linkLabel={t('ckpi.detailsIn', { modul: 'JV-Deckungsbeitrag' })}
          t={t}
        />
        <KpiCard
          label={t('ckpi.fcMarge')}
          value={pa.finecom_marge_pct != null ? `${fmt1(pa.finecom_marge_pct)}%` : '—'}
          sub={pa.hat_daten ? `WS: €${fmt(pa.finecom_weship)} / FC: €${fmt(pa.finecom_fc)}` : t('ckpi.noDaten')}
          info={{
            formel: pa.hat_daten ? `WeShip-Anteil ÷ Gesamt = €${fmt(pa.finecom_weship)} ÷ €${fmt(pa.finecom_umsatz)}` : 'Keine Abrechnung vorhanden',
            quellen: ['finecom_abrechnungen (monatliche Abrechnung)', 'finecom_revshare (Verteilungssätze)'],
            interpretation: pa.hat_daten ? `WeShip behält ${fmt1(pa.finecom_marge_pct)}% der Finecom-Abrechnung` : 'Noch keine Finecom-Abrechnung für diesen Monat'
          }}
          link="/finecom" linkLabel={t('ckpi.detailsIn', { modul: 'Finecom' })}
          t={t}
        />
      </div>
    </div>
  );
}
